import { createFileRoute } from "@tanstack/react-router";
import HashListDetailPage from "src/components/app/HashListPage/HashListDetailPage";
import PortalPage from "src/components/app/PortalPage/PortalPage";
import ContentPanel from "src/components/common/ContentPanel/ContentPanel";
import Skeleton from "src/components/common/Skeleton/Skeleton";
import LoadingStatus from "src/components/status/LoadingStatus/LoadingStatus";
import { DeferredLoaderData } from "src/lib/LoaderData";
import { raceLoaderData } from "src/lib/LoaderResult";
import { scopeFrom } from "src/lib/ScopedLoaders";
import { getHashListPageData } from "src/server/functions/Portal";
import { useExtracted } from "#i18n";

// A hash list the visitor can't see (or that doesn't exist) is a 404.
export const Route = createFileRoute("/_authed/portal/proj_{$projectKey}/hash-lists/$hashListId")({
  loader: (context) =>
    raceLoaderData(
      scopeFrom(context).then(({ projectId }) =>
        getHashListPageData({
          data: { projectId: projectId ?? "", hashListId: context.params.hashListId },
        }),
      ),
    ),
  component: HashListRoute,
});

function HashListRoute() {
  const { hashListId } = Route.useParams();
  return (
    // Another list starts from its own data, not the last list's.
    <DeferredLoaderData key={hashListId} result={Route.useLoaderData()}>
      {(data) =>
        !data ? (
          <HashListPageLoading />
        ) : data.hashList ? (
          <HashListDetailPage
            key={data.hashList.id}
            initialHashList={data.hashList}
            initialEntriesPage={data.entriesPage}
          />
        ) : // Signed out, the `_authed` layout renders the sign-in page instead.
        null
      }
    </DeferredLoaderData>
  );
}

/** The hash list page while its data is still loading (client navigations past the loader cut-off). */
function HashListPageLoading() {
  const translateMessage = useExtracted();
  return (
    <PortalPage>
      <PortalPage.Header>
        <PortalPage.Heading>
          <PortalPage.Title>
            <Skeleton width={240} aria-hidden="true" />
          </PortalPage.Title>
        </PortalPage.Heading>
      </PortalPage.Header>
      <PortalPage.Content>
        <ContentPanel>
          <ContentPanel.Content>
            <LoadingStatus title={translateMessage("Loading hash list...")} />
          </ContentPanel.Content>
        </ContentPanel>
      </PortalPage.Content>
    </PortalPage>
  );
}
